import type { ContestSettings } from '@betterpool/api-types/contests-service';
import { ContestSegmentType, SlateLabelEnum } from '@betterpool/api-types/contests-service';
import { format } from 'date-fns';
import useTranslation from 'next-translate/useTranslation';

import type { ContestSlate } from '~/domains/games/interfaces/contest.slates.interface';

import { SegmentTab } from './useSegmentedTabs';

type UseGetSegmentedDataArgs = {
  segments?: ContestSettings['segments'];
  activeTab: SegmentTab;
  slateLabel: SlateLabelEnum;
  slates: ContestSlate[];
  groupStageSlateId?: string;
};

const useGetSegmentedData = ({
  segments,
  activeTab,
  slateLabel,
  slates,
  groupStageSlateId,
}: UseGetSegmentedDataArgs) => {
  const { t } = useTranslation('contest');

  const findFirstSlate = (slateId) => slates.find(({ id }) => slateId === id);

  const segmentData =
    segments?.data.map((segment, i) => {
      const data = segment.slates
        .map((slateId) => slates.find(({ id }) => id === slateId))
        .map(({ name, start_date }) => ({ startDate: start_date, name }));

      const getLabel = () => {
        const segmentName = segment.name ?? `Segment ${i + 1}`;
        if (slateLabel === SlateLabelEnum.TOURNAMENT)
          return t('detail.schedule.table.tournamentName', { segmentName, count: data.length });
        if (slateLabel === SlateLabelEnum.WEEK)
          return t('detail.schedule.table.weekName', { segmentName, count: data.length });
        if (slateLabel === SlateLabelEnum.SLATE)
          return t('detail.schedule.table.slateName', { segmentName, count: data.length });
        return t('detail.schedule.table.slateName', { segmentName, count: data.length });
      };

      return {
        value: segment.id,
        label: getLabel(),
        startDate: format(findFirstSlate(segment.slates[0])?.start_date, "MM/dd '@' h:mm a"),
        data,
        isExpandable: data.length > 1,
      };
    }) ?? [];

  const mappedSlates = slates.map(({ id, name, start_date, games }) => {
    const data = games.map(({ name: gameName, start_date: gameStartDate }) => ({
      startDate: gameStartDate,
      name: gameName,
    }));
    const isGroupStageSlate = id === groupStageSlateId;

    const getLabel = () => {
      if (slateLabel === SlateLabelEnum.TOURNAMENT)
        return t('detail.schedule.table.singleTournamentName', { name, count: data.length });
      if (slateLabel === SlateLabelEnum.WEEK)
        return t('detail.schedule.table.singleWeekName', { name, count: data.length });
      if (slateLabel === SlateLabelEnum.SLATE)
        return t('detail.schedule.table.singleSlateName', { name, count: data.length });
      return t('detail.schedule.table.singleSlateName', { name, count: data.length });
    };

    return {
      value: id,
      label: data.length > 1 && !isGroupStageSlate ? getLabel() : name,
      startDate: format(new Date(start_date), "MM/dd '@' h:mm a"),
      data,
      isExpandable: data.length > 1 && !isGroupStageSlate,
    };
  });

  const items =
    activeTab === SegmentTab.OVERALL && segments?.segmentType === ContestSegmentType.MULTI_SLATE
      ? segmentData
      : mappedSlates;

  return items;
};

export default useGetSegmentedData;
